import React, { useRef, useState, useEffect } from 'react';
import { Box, Paper, Typography, useTheme, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useRadiogroupData } from '../../usePlotData';
import { BarChartComponent } from './BarChart';
import { RadialChartComponent } from './RadialChart';
import { CustomTooltip } from './CustomTooltip';
import { CHART_TYPES, CHART_DIMENSIONS, TEXT_STYLES, PAPER_STYLES, TITLE_STYLES } from './styles';
import { useTranslation } from 'react-i18next';

const truncateLabel = (label) => {
    if (!label) return '';
    
    const words = label.split(/\s+/);
    if (words.length <= 1) {
        if (label.length > 40) {
            const chunks = label.match(/.{1,40}/g) || [];
            return chunks;
        }
        return label;
    }

    const MAX_LINE_LENGTH = 40;
    const MAX_WORD_LENGTH = 40;
    
    let lines = [];
    let currentLine = [];
    let currentLength = 0;

    for (let word of words) {
        if (word.length > MAX_WORD_LENGTH) {
            if (currentLine.length > 0) {
                lines.push(currentLine.join(' '));
            }
            const chunks = word.match(new RegExp(`.{1,${MAX_WORD_LENGTH}}`, 'g')) || [];
            lines.push(...chunks);
            currentLine = [];
            currentLength = 0;
            continue;
        }

        const wordLength = word.length;
        
        if (currentLine.length === 0) {
            currentLine.push(word);
            currentLength = wordLength;
        } else if (currentLength + 1 + wordLength <= MAX_LINE_LENGTH) {
            currentLine.push(word);
            currentLength += 1 + wordLength;
        } else {
            lines.push(currentLine.join(' '));
            currentLine = [word];
            currentLength = wordLength;
        }
    }

    if (currentLine.length > 0) {
        lines.push(currentLine.join(' '));
    }

    return lines;
};

export const Radiogroup = ({ klausimas, atsakymai }) => {
    const { data } = useRadiogroupData(klausimas, atsakymai);
    const [chartType, setChartType] = useState(CHART_TYPES.BAR);
    const theme = useTheme();
    const chartRef = useRef(null);
    const { i18n } = useTranslation();
    const lng = i18n.language === 'en' ? 'default' : i18n.language;
    const [yAxisWidth, setYAxisWidth] = useState(300);

    useEffect(() => {
        const calculateYAxisWidth = () => {
            const screenWidth = window.innerWidth;
            const calculatedWidth = Math.min(Math.max(screenWidth * 0.25, 200), 300);
            setYAxisWidth(calculatedWidth);
        };

        calculateYAxisWidth();
        window.addEventListener('resize', calculateYAxisWidth);
        
        return () => {
            window.removeEventListener('resize', calculateYAxisWidth);
        };
    }, []);

    if (!data) return null;

    const renderYAxisTick = ({ x, y, payload }) => {
        const lines = truncateLabel(payload.value);
        const lineHeight = CHART_DIMENSIONS.ITEM.LINE_HEIGHT;
        const totalHeight = (Array.isArray(lines) ? lines.length - 1 : 0) * lineHeight;
        const startY = y - (totalHeight / 2);

        if (!Array.isArray(lines)) {
            return (
                <text
                    x={x}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    style={{
                        ...TEXT_STYLES.AXIS_TICK,
                        fill: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                    }}
                >
                    {lines}
                </text>
            );
        }

        return (
            <g>
                {lines.map((line, index) => (
                    <text
                        key={index}
                        x={x}
                        y={startY + (index * lineHeight)}
                        textAnchor="end"
                        dominantBaseline="middle"
                        style={{
                            ...TEXT_STYLES.AXIS_TICK,
                            fill: theme.palette.text.primary,
                            fontFamily: theme.typography.fontFamily,
                        }}
                    >
                        {line}
                    </text>
                ))}
            </g>
        );
    };

    const renderContent = () => {
        switch (chartType) {
            case CHART_TYPES.TABLE:
                return (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: theme.palette.grey[100],
                                            fontSize: '1rem'
                                        }}
                                    >
                                        Atsakymas
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            backgroundColor: theme.palette.grey[100],
                                            fontSize: '1rem'
                                        }}
                                    >
                                        Procentai
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((row, index) => (
                                    <TableRow 
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.grey[50],
                                            }
                                        }}
                                    >
                                        <TableCell 
                                            component="th" 
                                            scope="row"
                                            sx={{ fontSize: '1rem' }}
                                        >
                                            {row.text}
                                        </TableCell>
                                        <TableCell 
                                            align="center"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontWeight: row.value > 50 ? 'bold' : 'normal',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {row.value.toFixed(1)}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            case CHART_TYPES.BAR:
                return (
                    <BarChartComponent 
                        data={data} 
                        CustomTooltip={CustomTooltip} 
                        renderYAxisTick={renderYAxisTick}
                        yAxisWidth={yAxisWidth}
                        theme={theme}
                    />
                );
            default:
                return <RadialChartComponent data={data} CustomTooltip={CustomTooltip} />;
        }
    };

    return (
        <Box ref={chartRef} data-chart-type="radiogroup" sx={{ width: '100%' }}>
            <Paper
                elevation={0}
                sx={{
                    ...PAPER_STYLES.CONTAINER,
                    '@media print, [data-export="true"]': {
                        fontFamily: theme.typography.fontFamily,
                        '& *': {
                            fontFamily: 'inherit !important'
                        },
                        '& .recharts-text': {
                            fontSize: '20px !important',
                            fontWeight: '500 !important'
                        }
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1, gap: 2 }}>
                    <Typography sx={{
                        ...TEXT_STYLES.TITLE,
                        ...TITLE_STYLES,
                        color: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily,
                        maxWidth: '100%'
                    }}>
                        {klausimas.title?.[lng] || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flex: 1 }}>
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>{i18n.t('chart_type')}</InputLabel>
                            <Select
                                value={chartType}
                                label={i18n.t('chart_type')}
                                onChange={(e) => setChartType(e.target.value)}
                            >
                                <MenuItem value={CHART_TYPES.BAR}>{i18n.t('bar_chart')}</MenuItem>
                                <MenuItem value={CHART_TYPES.RADIAL}>{i18n.t('radial_chart')}</MenuItem>
                                <MenuItem value={CHART_TYPES.TABLE}>Lentelė</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {renderContent()}
            </Paper>
        </Box>
    );
};
